import { and, not, notEq } from 'tio-ui/utilities';
import { Divider } from 'tio-ui/components/utilities';
import ActionableTranslation from 'tio-ui/components/actionable-translation';
import { dropTask } from 'ember-concurrency';
import { fn, concat } from '@ember/helper';
import Modal from 'tio-reimagined/components/modal';
import Footer from 'tio-reimagined/components/footer';
import { formatAsDollars } from '../../../utils/format.ts';
import * as v from 'valibot';
import { on } from '@ember/modifier';
import { service } from '@ember/service';
import { t } from 'ember-intl';
import { tracked } from '@glimmer/tracking';
import { hash } from '@ember/helper';
import Component from '@glimmer/component';
import CustomFieldsApproverReview from '../custom-fields-approver-review.gts';
import DefinitionList from '../../ui/definition-list.gts';
import { divide } from 'tio-ui/math';
import EditCourseDurationDialog from './edit-course-duration-dialog.gts';
import errorsForField from '../../../helpers/errors-for-field.ts';
import formatDateString from '../../../helpers/format-date-string.ts';
import Input from 'tio-reimagined/components/input';
import Textarea from 'tio-reimagined/components/textarea';
import inputmask from '../../../modifiers/inputmask.ts';
import ProgramReadonlyField from '../program-readonly-field.gts';
import ReimbursementCalculationIfApplicable from '../reimbursement-calculation-if-applicable.gts';
import safeFormatDate from '../../../helpers/safe-format-date.ts';
import safeFormatNumber from '../../../helpers/safe-format-number.ts';
import TioErrorMessages from '../../tio/error-messages.gts';
import type { ErrorsSignature } from '../../tio/error-messages.gts';
import type {
  TasApplicationState,
  TasProgramInstanceState,
  ApplicationApprovalSummary,
} from '../../../types/tuition-assistance';
import type Inputmask from 'inputmask';
import type Intl from 'ember-intl/services/intl';
import type { NotificationsService } from 'tio-reimagined/components/notifications';
import type RoleViewModel from '../../../models/role-view';
import type Router from '@ember/routing/router-service';
import type SessionContextService from '../../../services/session-context.ts';
import type Store from '@ember-data/store';
import type TasApplication from '../../../models/tas-application.ts';
import type TASProgramInstanceModel from '../../../models/tas-program-instance.ts';
import { Section } from 'tio-ui/components/layout';
import Button from 'tio-reimagined/components/button';
import Chip from 'tio-reimagined/components/chip';
import Checkbox from 'tio-reimagined/components/checkbox';
import { PhPencil, PhCaretDown, PhCaretUp } from 'ember-phosphor-icons';

const repaymentRequestFormSchema = v.object({
  amount: v.pipe(
    v.number(),
    v.integer('Amount must be an integer'),
    v.minValue(1, 'Amount must be more than $0')
  ),
  reason: v.pipe(v.string(), v.nonEmpty('Please enter a reason for the repayment request')),
});

export interface TuitionAssistanceProgramDetailsApplicationInformationSignature {
  Args: {
    application: TasApplication | TASProgramInstanceModel;
    isAdminView: boolean;
    canRequestRepayment: boolean;
    canEditTasApplication?: boolean;
    isReadonlyView?: boolean;
    requestingApp?: string;
  };
  Element: HTMLElement;
}

interface actionMapSignature {
  [key: string]: string;
}

const alwaysRescindableStates = new Set([
  'TAS.ProgramInstanceState.PENDING_PROGRAM_APPROVAL',
  'TAS.ProgramInstanceState.PENDING_COMPLETION_APPROVAL',
  'TAS.ProgramInstanceState.PENDING_WITHDRAWAL_APPROVAL',
  'TAS.ProgramInstanceState.PRIMED',
  'TAS.ApplicationState.PENDING_COURSES_APPROVAL',
  'TAS.ApplicationState.PENDING_EVIDENCE_APPROVAL',
]);

export default class TuitionAssistanceProgramDetailsApplicationInformationComponent extends Component<TuitionAssistanceProgramDetailsApplicationInformationSignature> {
  @service declare store: Store;
  @service declare notifications: NotificationsService;
  @service declare intl: Intl;
  @service declare router: Router;
  @service declare sessionContext: SessionContextService;

  @tracked didSubmitRepaymentRequest = false;
  @tracked requestedRepaymentAmount = 0;
  @tracked reasonForRepaymentRequest = '';
  @tracked showReopenDialog = false;
  @tracked showRepaymentDialog = false;
  @tracked didSubmitSettlementRequest = false;
  @tracked showSettlePaymentDialog = false;
  @tracked requestedSettlementAmount = 0;
  @tracked reasonForSettlementRequest = '';
  @tracked showForgivePaymentDialog = false;
  @tracked requestedForgivenessAmount = 0;
  @tracked reasonForForgivenessRequest = '';
  @tracked didSubmitForgivenessRequest = false;
  @tracked submitForgivenessError = null;
  @tracked submitError: string | ErrorsSignature | null = null;
  @tracked submitSettlementError = null;
  @tracked showEditCourseDurationDialog = false;
  @tracked pauseEmployee = true;
  @tracked showRescindDialog = false;
  @tracked didSubmitRescindRequest = false;
  @tracked approvalLog: string | null = null;
  @tracked showIgnorePoliciesModal = false;

  get settleOrForgiveRequestFormSchema() {
    return v.object({
      amount: v.pipe(
        v.number(),
        v.integer('Amount must be an integer'),
        v.minValue(1, 'Amount must be more than $0'),
        v.maxValue(this.numericalAmountOwed, 'Amount cannot be more than what is owed.')
      ),
      reason: v.pipe(v.string(), v.nonEmpty('Please provide a note regarding this payment')),
    });
  }
  @tracked isExpanded = false;

  get personId() {
    return this.tasApplicationModel?.tasProgramInstance?.employee?.person?.id || '';
  }

  get isModelTasApplication() {
    return this.tasApplicationModel !== undefined;
  }

  get tasApplicationModel(): TasApplication | undefined {
    return this.args.application?.modelName === 'tas-application'
      ? (this.args.application as TasApplication)
      : undefined;
  }

  get programTemplate() {
    const application = this.args.application;
    if (!application) {
      return undefined; // or handle accordingly if application can be null or undefined
    }
    return application.modelName === 'tas-application'
      ? application.tasProgramInstance.tasProgramTemplate
      : application.tasProgramTemplate;
  }

  get isCertificateProgram() {
    return this.programTemplate?.isCertificateProgram ?? false;
  }

  get state(): TasApplicationState | TasProgramInstanceState {
    return this.args.application.state;
  }

  get isRejected() {
    const rejectedStates = [
      'TAS.ProgramInstanceState.PROGRAM_REJECTED',
      'TAS.ProgramInstanceState.COMPLETION_REJECTED',
      'TAS.ProgramInstanceState.WITHDRAWAL_REJECTED',
      'TAS.ApplicationState.COURSES_REJECTED',
      'TAS.ApplicationState.EVIDENCE_REJECTED',
      'TAS.ApplicationState.REJECTED_PER_POLICY',
    ];

    return rejectedStates.includes(this.state);
  }

  get setAdapter() {
    const splitState = this.state.split('.').slice(0, 2).join('.');
    return splitState === 'TAS.ProgramInstanceState'
      ? 'tasProgramInstance'
      : splitState === 'TAS.ApplicationState'
        ? 'tasApplication'
        : undefined;
  }

  get adapterMethod() {
    const actionMap: actionMapSignature = {
      'TAS.ProgramInstanceState.PROGRAM_REJECTED': 'requestChanges',
      'TAS.ProgramInstanceState.COMPLETION_REJECTED': 'requestProgramCompletionChanges',
      'TAS.ProgramInstanceState.WITHDRAWAL_REJECTED': 'requestProgramWithdrawalChanges',
      'TAS.ApplicationState.COURSES_REJECTED': 'requestChanges',
      'TAS.ApplicationState.EVIDENCE_REJECTED': 'requestCourseEvidenceChanges',
      'TAS.ApplicationState.REJECTED_PER_POLICY': 'rescindLastApproverAction',
    };
    return actionMap[this.state] || '';
  }

  get showPaymentState() {
    const app = this.tasApplicationModel;
    return !!(app && app.paymentState && app.paymentState !== 'TAS.ApplicationPaymentState.NONE');
  }

  get showRepaymentButton() {
    const app = this.tasApplicationModel;
    return !!(
      app &&
      this.args.canRequestRepayment &&
      app.canRequestRepayment &&
      ['TAS.ApplicationState.FULFILLED', 'TAS.ApplicationState.EVIDENCE_REJECTED'].includes(
        app.state
      ) &&
      app.paidAndForgivenTotal > 0
    );
  }

  get showInCollectionsCheckbox() {
    // TODO: Clarify this logic
    return this.isModelTasApplication && this.args.isAdminView;
  }

  get userCanUnrejectApp() {
    const application = this.args.application;
    const isTasApplication = application.modelName === 'tas-application';
    let log: ApplicationApprovalSummary | undefined = undefined;

    // coursesApproval/evidenceApproval live on TasApplication;
    // programApproval/withdrawalApproval live on TASProgramInstance.
    switch (this.state) {
      case 'TAS.ApplicationState.COURSES_REJECTED':
        if (isTasApplication) log = application.coursesApproval;
        break;
      case 'TAS.ApplicationState.EVIDENCE_REJECTED':
        if (isTasApplication) log = application.evidenceApproval;
        break;
      case 'TAS.ProgramInstanceState.PROGRAM_REJECTED':
        if (!isTasApplication) log = application.programApproval;
        break;
      case 'TAS.ProgramInstanceState.WITHDRAWAL_REJECTED':
        if (!isTasApplication) log = application.withdrawalApproval;
        break;
      case 'TAS.ProgramInstanceState.COMPLETION_REJECTED':
        if (!isTasApplication) log = application.withdrawalApproval;
        break;
      default:
        log = undefined;
    }

    const approverRoleType = log?.current_reviewers?.[0]?.dot;
    // No current reviewer ⇒ nothing to match against ⇒ deny. This is a
    // deliberate behaviour change from the previous '=== approverRoleType'
    // comparison, which would have returned true when both sides were
    // undefined. Deny-by-default is safer for permissions.
    if (!approverRoleType) return false;

    if (this.args.requestingApp === 'employee') {
      if (this.sessionContext.user?.isAccountOwnerOrEmployeeAdmin) return true;
      const userRoleType = this.sessionContext.currentRole?.relationshipType;

      return userRoleType === approverRoleType;
    } else {
      if (this.sessionContext.user?.isTioAdmin) return true;
      const userRolesRelationshipTypes =
        this.sessionContext.user.uniqueRolesByRelationshipType.map(
          (roleView: RoleViewModel) => roleView.relationshipType
        ) || [];

      return userRolesRelationshipTypes.includes(approverRoleType);
    }
  }

  get secondToLastStateTransition() {
    const log = this.args.application.transitionLog;
    if (log && log.length >= 2) {
      return log[log.length - 2];
    }
    return null;
  }

  get userCanRescindApp() {
    const approverRoleType =
      this.secondToLastStateTransition?.reason?.by?.actingWithRole?.relationshipType;
    // No prior approver role on the transition log ⇒ there is nothing to
    // rescind against ⇒ deny. Deliberate behaviour change from the previous
    // '=== approverRoleType' comparison, which would have returned true when
    // both sides were undefined. Deny-by-default is safer for permissions.
    if (!approverRoleType) return false;
    const { requestingApp } = this.args;
    const sessionUser = this.sessionContext.user;

    if (requestingApp === 'employee') {
      if (sessionUser?.isAccountOwnerOrEmployeeAdmin) {
        return true;
      }
      return this.sessionContext.currentRole?.relationshipType === approverRoleType;
    }

    const userRolesRelationshipTypes =
      sessionUser?.uniqueRolesByRelationshipType?.map(
        (roleView: RoleViewModel) => roleView.relationshipType
      ) || [];

    if (requestingApp === 'tio-admin') {
      return userRolesRelationshipTypes.includes('TAS.Admin.SUPERUSER');
    }

    return userRolesRelationshipTypes.includes(approverRoleType);
  }

  rescindStateAction = dropTask(async () => {
    this.submitError = null;
    const adapter = this.store.adapterFor(this.setAdapter);

    try {
      await adapter.rescindLastApproverAction(this.args.application);
      this.showRescindDialog = false;
      await this.args.application.reload();
      const successMessage = this.intl.t('common.success');
      this.notifications.add(successMessage, {
        appearance: 'success',
      });
    } catch (e) {
      console.error(e);
      this.submitError = this.intl.t('tuition_assistance.application_details.rescind.error');
    }
  });

  get showRescindButton() {
    return this.args.isAdminView && this.inRescindableState && this.userCanRescindApp;
  }

  get inRescindableState() {
    const { state, inPaidState } = this;
    if (alwaysRescindableStates.has(state)) {
      return true;
    }

    if (state === 'TAS.ApplicationState.ATTEND' || state === 'TAS.ApplicationState.FULFILLED') {
      return !inPaidState;
    }

    return false;
  }

  get showReopenApplicationButton() {
    return (
      this.isRejected &&
      this.args.isAdminView &&
      !this.args.isReadonlyView &&
      this.userCanUnrejectApp
    );
  }

  get showEditCourseDurationDialogToAdmin() {
    return this.args.canEditTasApplication;
  }

  get inPaidState() {
    return this.tasApplicationModel?.isPaid;
  }

  get paymentInfoText() {
    const property = this.inPaidState ? 'paid' : 'approved';

    return {
      amount: this.intl.t(`common.tuition_assistance.application_details.${property}_amount`),
      date: this.intl.t(`common.tuition_assistance.application_details.${property}_date`),
    };
  }

  get employee() {
    const application = this.args.application;
    return application.modelName === 'tas-application'
      ? application.tasProgramInstance.employee
      : application.employee;
  }

  get participantId() {
    return this.employee.tasParticipant.id;
  }

  get showViewAttachmentsSection() {
    return (
      this.args.application.tasAssets?.length > 0 || this.args.application.customFields?.length > 0
    );
  }

  get lastTransitionLogAutoRejectedFromAttend() {
    const log = this.args.application.transitionLog;
    const lastTransition = log?.at(-1);
    return (
      log?.length >= 2 &&
      lastTransition?.to === 'TAS.ApplicationState.EVIDENCE_REJECTED' &&
      lastTransition.from === 'TAS.ApplicationState.ATTEND'
    );
  }

  get lastApprovedTotalBeforeRejection() {
    return this.tasApplicationModel?.approvedTotalLog?.at(-1)?.from;
  }

  reopenApplication = dropTask(async () => {
    this.submitError = null;
    const adapter = this.store.adapterFor(this.setAdapter);
    try {
      await adapter[this.adapterMethod](this.args.application);

      // If the application was auto-rejected from the attend state
      if (this.lastTransitionLogAutoRejectedFromAttend) {
        // reset the approved total to the amount before the auto-rejection
        await adapter.updateApprovedTotal(
          this.args.application,
          this.lastApprovedTotalBeforeRejection,
          'Reset total to amount prior to auto-rejection'
        );
      }

      this.showReopenDialog = false;
      const successMessage = this.intl.t('common.success');
      this.notifications.add(successMessage, {
        appearance: 'success',
      });
    } catch (e) {
      console.error(e);
      this.submitError = e;
    }
  });

  requestRepayment = dropTask(async () => {
    this.submitError = null;
    const adapter = this.store.adapterFor('tas-application');
    const { amount, reason } = this.repaymentFormModel;

    try {
      await adapter.requestRepayment(this.args.application, amount, reason);
      if (
        this.pauseEmployee &&
        this.employee.tasParticipant.state !== 'TAS.ParticipantState.HELD'
      ) {
        await this.putEmployeeOnHold();
      }
      this.resetRepaymentForm();
    } catch (e) {
      console.error(e);
      this.submitError = e;
    }
  });

  requestSettlementRepayment = dropTask(async () => {
    this.submitSettlementError = null;
    const adapter = this.store.adapterFor('tas-application');
    const { amount, reason } = this.settlementFormModel;

    try {
      await adapter.recordRepayment(this.args.application, amount, reason);
      this.resetSettlementForm();
    } catch (e) {
      console.error(e);
      this.submitSettlementError = e;
    }
  });

  requestForgivenessRepayment = dropTask(async () => {
    this.submitForgivenessError = null;
    const adapter = this.store.adapterFor('tas-application');
    const { amount, reason } = this.forgivenessFormModel;

    try {
      await adapter.forgiveRepayment(this.args.application, amount, reason);
      this.resetForgivenessForm();
    } catch (e) {
      console.error(e);
      this.submitForgivenessError = e;
    }
  });

  toggleRepaymentDialog = () => {
    this.showRepaymentDialog = !this.showRepaymentDialog;
  };

  submitRepaymentForm = () => {
    this.didSubmitRepaymentRequest = true;
    if (this.repaymentFormValidationErrors.length) {
      return;
    }
    this.requestRepayment.perform();
  };

  submitRescindRequest = () => {
    this.didSubmitRescindRequest = true;
    this.rescindStateAction.perform();
  };

  resetRepaymentForm = () => {
    this.showRepaymentDialog = false;
    this.didSubmitRepaymentRequest = false;
    this.reasonForRepaymentRequest = '';
    this.requestedRepaymentAmount = 0;
  };

  updateRepaymentAmount = (_value: unknown, event?: InputEvent) => {
    if (!event) return;
    const target = event.target as HTMLInputElement & { inputmask?: Inputmask.Instance };
    const unmaskedValue = target.inputmask?.unmaskedvalue() ?? '';
    this.requestedRepaymentAmount = Math.round(Number(unmaskedValue) * 100);
  };

  updateRepaymentReason = (value = '') => {
    this.reasonForRepaymentRequest = value;
  };

  toggleSettlePaymentDialog = () => {
    this.showSettlePaymentDialog = !this.showSettlePaymentDialog;
  };

  toggleForgivePaymentDialog = () => {
    this.showForgivePaymentDialog = !this.showForgivePaymentDialog;
  };

  submitForgivenessRepaymentForm = () => {
    this.didSubmitForgivenessRequest = true;
    if (this.forgivenessFormValidationErrors.length) {
      return;
    }
    this.requestForgivenessRepayment.perform();
  };

  submitSettleRepaymentForm = () => {
    this.didSubmitSettlementRequest = true;
    if (this.settlementFormValidationErrors.length) {
      return;
    }
    this.requestSettlementRepayment.perform();
  };

  resetSettlementForm = () => {
    this.showSettlePaymentDialog = false;
    this.didSubmitSettlementRequest = false;
    this.reasonForSettlementRequest = '';
    this.requestedSettlementAmount = 0;
  };

  resetForgivenessForm = () => {
    this.showForgivePaymentDialog = false;
    this.didSubmitForgivenessRequest = false;
    this.reasonForForgivenessRequest = '';
    this.requestedForgivenessAmount = 0;
  };

  updateSettlementAmount = (_value: unknown, event?: InputEvent) => {
    if (!event) return;
    const target = event.target as HTMLInputElement & { inputmask?: Inputmask.Instance };
    const unmaskedValue = target.inputmask?.unmaskedvalue() ?? '';
    this.requestedSettlementAmount = Math.round(Number(unmaskedValue) * 100);
  };

  updateSettlementReason = (value = '') => {
    this.reasonForSettlementRequest = value;
  };

  updateForgivenessAmount = (_value: unknown, event?: InputEvent) => {
    if (!event) return;
    const target = event.target as HTMLInputElement & { inputmask?: Inputmask.Instance };
    const unmaskedValue = target.inputmask?.unmaskedvalue() ?? '';
    this.requestedForgivenessAmount = Math.round(Number(unmaskedValue) * 100);
  };

  updateForgivenessReason = (value = '') => {
    this.reasonForForgivenessRequest = value;
  };

  toggleInCollections = () => {
    if (this.isModelTasApplication) {
      this.tasApplicationModel?.toggleInCollections();
    }
  };
  get participantAdapter() {
    return this.store.adapterFor('tas-participant');
  }

  togglePauseEmployee = async () => {
    this.pauseEmployee = !this.pauseEmployee;
    if (this.pauseEmployee) {
      await this.putEmployeeOnHold();
    } else {
      await this.removeTasParticipantHold();
    }
  };

  removeTasParticipantHold = async () => {
    await this.participantAdapter.removeTasParticipantHold(this.participantId);
  };

  putEmployeeOnHold = async () => {
    await this.participantAdapter.holdTasParticipant(this.participantId);
  };

  openIgnorePoliciesModal = () => {
    const app = this.tasApplicationModel;
    if (!app) return;
    app.ignorePolicies = true;
    this.showIgnorePoliciesModal = true;
  };

  setIgnorePolicies = () => {
    const app = this.tasApplicationModel;
    if (!app) return;
    const today = this.intl.formatDate(new Date(), {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short',
    });
    const changelogEntry = `Ignore Policies set by ${this.sessionContext.user.person.fullName} on ${today}`;
    app.ignorePolicies = true;
    app.save({ adapterOptions: { changelog: changelogEntry } });
    this.showIgnorePoliciesModal = false;
  };

  cancelIgnorePolicies = () => {
    const app = this.tasApplicationModel;
    if (!app) return;
    app.ignorePolicies = false;
    this.showIgnorePoliciesModal = false;
  };

  get settlementFormModel() {
    return {
      amount: this.requestedSettlementAmount,
      reason: this.reasonForSettlementRequest,
    };
  }

  get forgivenessFormModel() {
    return {
      amount: this.requestedForgivenessAmount,
      reason: this.reasonForForgivenessRequest,
    };
  }

  get settlementFormValidationErrors() {
    const schema = this.settleOrForgiveRequestFormSchema;
    const result = v.safeParse(schema, this.settlementFormModel);
    if (!result.success) {
      const flattened = v.flatten(result.issues);
      const nested = flattened.nested ?? {};
      return Object.entries(nested).map(([field, messages]) => ({
        path: field,
        errors: messages ?? [],
        value: this.settlementFormModel[field as keyof typeof this.settlementFormModel] as string,
      }));
    }
    return [];
  }

  get forgivenessFormValidationErrors() {
    const schema = this.settleOrForgiveRequestFormSchema;
    const result = v.safeParse(schema, this.forgivenessFormModel);
    if (!result.success) {
      const flattened = v.flatten(result.issues);
      const nested = flattened.nested ?? {};
      return Object.entries(nested).map(([field, messages]) => ({
        path: field,
        errors: messages ?? [],
        value: this.forgivenessFormModel[field as keyof typeof this.forgivenessFormModel] as string,
      }));
    }
    return [];
  }

  get repaymentFormModel() {
    return {
      amount: this.requestedRepaymentAmount,
      reason: this.reasonForRepaymentRequest,
    };
  }

  get repaymentFormValidationErrors() {
    const schema = repaymentRequestFormSchema;
    const result = v.safeParse(schema, this.repaymentFormModel);
    if (!result.success) {
      const flattened = v.flatten(result.issues);
      const nested = flattened.nested ?? {};
      return Object.entries(nested).map(([field, messages]) => ({
        path: field,
        errors: messages ?? [],
        value: this.repaymentFormModel[field as keyof typeof this.repaymentFormModel] as string,
      }));
    }
    return [];
  }

  get isRepaymentFormValid() {
    return !this.repaymentFormValidationErrors.length;
  }

  get isSettlementFormValid() {
    return !this.settlementFormValidationErrors.length;
  }

  get isForgivenessFormValid() {
    return !this.forgivenessFormValidationErrors.length;
  }

  get tasAssistances() {
    return this.tasApplicationModel?.tasAssistances;
  }

  get numericalAmountOwed() {
    if (!this.tasAssistances) {
      return 0;
    }
    const amount = [];
    for (const assistance of this.tasAssistances) {
      if (assistance.state === 'TAS.AssistanceState.REPAYABLE') {
        amount.push(Math.abs(assistance.amount));
      }
    }
    return amount.reduce((a, b) => a + b, 0);
  }

  get amountOwed() {
    if (!this.tasAssistances) {
      return 0;
    }
    const amount = [];
    for (const assistance of this.tasAssistances) {
      if (assistance.state === 'TAS.AssistanceState.REPAYABLE') {
        amount.push(Math.abs(assistance.amount));
      }
    }
    return formatAsDollars(amount.reduce((a, b) => a + b, 0));
  }

  statusText = (state: string, type: string): string => {
    const baseKey =
      type === 'certificate' ? 'common.certificate_status_message' : 'common.status_message';
    const key = `${baseKey}.to.${state}`;

    if (this.intl.exists(key)) {
      return this.intl.t(key);
    }

    return this.intl.t('common.status_message.to.TAS.ProgramInstanceState.UPDATED_ON');
  };

  // Only meaningful when `this.tasApplicationModel` is present; 0 fallback is
  // a safe no-op for the template arithmetic when this is a program-instance.
  get approvedTotal() {
    return this.tasApplicationModel?.approvedTotal ?? 0;
  }

  get paidTotal() {
    return this.tasApplicationModel?.paidTotal ?? 0;
  }

  <template>
    <Section>
      <:header>
        <p data-test-application-information>
          {{t "tuition_assistance.program_details.application_information"}}
          {{! @glint-expect-error}}
          {{#if @application.isRefundRequested}}
            <Chip @size="sm" @intent="danger" class="ml-2">
              {{t "tuition_assistance.application_details.in_repayment"}}
            </Chip>
          {{/if}}
          {{! @glint-expect-error}}
          {{#each @application.tags as |tag|}}
            <div
              class="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-600/20"
            >{{tag.name}}</div>
          {{/each}}
        </p>
      </:header>
      <:subHeader>
        <p data-test-application-information-id>
          {{t "tuition_assistance.id"}}
          {{@application.id}}
        </p>
      </:subHeader>
      <:actions>
        {{#if this.showEditCourseDurationDialogToAdmin}}
          <Button
            @intent="primary"
            {{on "click" (fn (mut this.showEditCourseDurationDialog) true)}}
            title={{t "common.edit"}}
          >
            <PhPencil @size="16" />
          </Button>
        {{/if}}
      </:actions>
      <:body>
        <DefinitionList as |options|>
          <div class={{options.wrappingClass}}>
            <options.dt>
              {{this.statusText this.state "application"}}
            </options.dt>
            <options.dd>
              {{safeFormatDate @application.latestStateChangeDate}}
            </options.dd>
          </div>
          <div class={{options.wrappingClass}}>
            <options.dt>
              {{t "tuition_assistance.program_details.status.created"}}
            </options.dt>
            <options.dd>
              {{safeFormatDate @application.createdAt}}
            </options.dd>
          </div>
          <ProgramReadonlyField
            @name="COURSES_BEGIN_DATE"
            {{! @glint-expect-error}}
            @programTemplate={{@application.tasProgramInstance.tasProgramTemplate}}
            @fields={{@application.fields}}
            as |field|
          >
            <div class={{options.wrappingClass}}>
              <options.dt>{{field.programLabel}}</options.dt>
              {{#if field.value}}
                <options.dd>
                  {{formatDateString field.value}}
                </options.dd>
              {{else}}
                {{t "tuition_assistance.program_details.status.not_provided"}}
              {{/if}}
            </div>
          </ProgramReadonlyField>
          <ProgramReadonlyField
            @name="COURSES_RECEIPT_DATE"
            {{! @glint-expect-error}}
            @programTemplate={{@application.tasProgramInstance.tasProgramTemplate}}
            @fields={{@application.fields}}
            as |field|
          >
            <div class={{options.wrappingClass}}>
              <options.dt>{{field.programLabel}}</options.dt>
              <options.dd>
                {{formatDateString field.value}}
              </options.dd>
            </div>
          </ProgramReadonlyField>
          <ProgramReadonlyField
            @name="COURSES_END_DATE"
            {{! @glint-expect-error}}
            @programTemplate={{@application.tasProgramInstance.tasProgramTemplate}}
            @fields={{@application.fields}}
            as |field|
          >
            <div class={{options.wrappingClass}}>
              <options.dt>{{field.programLabel}}</options.dt>
              <options.dd>
                {{formatDateString field.value}}
              </options.dd>
            </div>
          </ProgramReadonlyField>
          <ProgramReadonlyField
            @name="SEMESTER_CODE"
            {{! @glint-expect-error}}
            @programTemplate={{@application.tasProgramInstance.tasProgramTemplate}}
            @fields={{@application.fields}}
            as |field|
          >
            <div class={{options.wrappingClass}}>
              <options.dt>{{field.programLabel}}</options.dt>
              <options.dd>
                {{! @glint-expect-error: formatDateString expects a string }}
                {{field.value}}
              </options.dd>
            </div>
          </ProgramReadonlyField>

          <div class={{options.wrappingClass}}>
            <options.dt>
              {{t "common.status"}}
            </options.dt>
            {{#if this.isCertificateProgram}}
              <options.dd>
                {{this.statusText @application.state "certificate"}}
              </options.dd>
            {{else}}
              <options.dd>
                {{this.statusText @application.state "application"}}
              </options.dd>
            {{/if}}
          </div>
          {{! @glint-expect-error}}
          {{#if @application.shouldDisplayApprovedTotal}}
            <div class={{options.wrappingClass}}>
              <options.dt>
                {{t "tuition_assistance.program_details.status.approved_total"}}
                <span class="block text-xs text-gray-700">
                  {{t "tuition_assistance.program_details.including_any_scholarships"}}
                </span>
              </options.dt>
              <options.dd>
                {{safeFormatNumber (divide this.approvedTotal 100) style="currency" currency="USD"}}
              </options.dd>
            </div>
            {{#unless this.inPaidState}}
              <div class={{options.wrappingClass}}>
                <options.dt>
                  {{this.paymentInfoText.date}}
                </options.dt>

                <options.dd>
                  {{! @glint-expect-error}}
                  {{safeFormatDate @application.approvedDate}}
                </options.dd>
              </div>
            {{/unless}}
            {{#if this.tasApplicationModel}}
              <div class={{options.wrappingClass}}>
                <ReimbursementCalculationIfApplicable @application={{this.tasApplicationModel}} />
              </div>
            {{/if}}
          {{/if}}
          {{#if this.showPaymentState}}
            <div class={{options.wrappingClass}}>
              <options.dt>
                {{t "common.payment_state"}}
              </options.dt>
              <options.dd>
                {{! @glint-expect-error}}
                {{t (concat "common.status_message.to." @application.paymentState)}}
              </options.dd>
            </div>
            {{#if this.inPaidState}}
              <div class={{options.wrappingClass}}>
                <options.dt>
                  {{this.paymentInfoText.amount}}
                </options.dt>
                <options.dd>
                  {{safeFormatNumber (divide this.paidTotal 100) style="currency" currency="USD"}}
                </options.dd>
              </div>
              <div class={{options.wrappingClass}}>
                <options.dt>
                  {{this.paymentInfoText.date}}
                </options.dt>
                <options.dd>
                  {{! @glint-expect-error}}
                  {{safeFormatDate @application.paidDate}}
                </options.dd>
              </div>
            {{/if}}
          {{/if}}
        </DefinitionList>

        <Divider class="my-2" />

        {{#if this.isExpanded}}
          <CustomFieldsApproverReview
            @customFields={{@application.customFields}}
            @assets={{@application.tasAssets}}
            @province="APPLICATION"
          />
        {{/if}}
        {{#if this.showViewAttachmentsSection}}
          <div class="flex justify-center mt-3">
            <button
              type="button"
              class="inline-flex items-center font-semibold text-sm text-tio-gray-600"
              {{on "click" (fn (mut this.isExpanded) (not this.isExpanded))}}
              data-test-view-attachments-button
            >
              {{#if this.isExpanded}}
                <PhCaretDown @size="16" class="mr-1" />
              {{else}}
                <PhCaretUp @size="16" class="mr-1" />
              {{/if}}
              {{t "tuition_assistance.application_details.view_application_attachments"}}
            </button>
          </div>
        {{/if}}

        {{#if this.showInCollectionsCheckbox}}
          <div class="mb-3 text-sm text-tio-gray-600">
            <Checkbox
              @label="In Collections"
              @onChange={{this.toggleInCollections}}
              {{! @glint-expect-error}}
              @checked={{@application.isInCollections}}
            />
          </div>
        {{/if}}

        {{#if this.showReopenApplicationButton}}
          <Divider class="my-9" />
          <button
            name="reopen-application"
            type="button"
            class="text-center tio-copy w-full"
            {{on "click" (fn (mut this.showReopenDialog) true)}}
          >
            {{t "tuition_assistance.reopen_application"}}
          </button>
        {{/if}}

        {{#if this.showRescindButton}}
          <Divider class="my-9" />
          <Button
            {{on "click" (fn (mut this.showRescindDialog) true)}}
            disabled={{not this.showRescindButton}}
            @intent="primary"
            class="uppercase min-w-48 mx-auto my-4 w-fit"
          >
            {{t "tuition_assistance.application_details.rescind.button"}}
          </Button>
        {{/if}}
        <div class="flex justify-end">
          {{#if (and @canEditTasApplication @isAdminView)}}
            {{! @glint-expect-error}}
            {{#if @application.ignorePolicies}}
              <div class="flex">
                <p class="text-red-600 mr-2">
                  <Chip @size="sm" @intent="danger">
                    {{t "tuition_assistance.application_details.policy_override"}}
                  </Chip>
                </p>
              </div>
            {{else}}
              <Button
                @intent="danger"
                @appearance="outlined"
                @size="sm"
                title={{t "common.tuition_assistance.application_details.policy_override_popover"}}
                class="w-24"
                {{on "click" this.openIgnorePoliciesModal}}
              >
                {{t "common.tuition_assistance.application_details.policy_override_prompt"}}
              </Button>
            {{/if}}
          {{/if}}
        </div>
      </:body>
    </Section>
    <Modal
      @isOpen={{this.showReopenDialog}}
      @onClose={{fn (mut this.showReopenDialog) false}}
      @ariaLabel={{t "common.aria_labels.reopen_application"}}
    >
      <:body>
        <p class="text-xl my-4 text-tio-gray-800">
          {{t "tuition_assistance.application_details.would_you_like_to_reopen_this_application"}}
        </p>
      </:body>
      <:footer>
        <Footer
          @submitText={{t "common.confirm"}}
          @onSubmit={{this.reopenApplication.perform}}
          @isRunning={{this.reopenApplication.isRunning}}
          @onCancel={{fn (mut this.showReopenDialog) false}}
        />
      </:footer>
    </Modal>

    <Modal
      @isOpen={{this.showRescindDialog}}
      @onClose={{fn (mut this.showRescindDialog) false}}
      @ariaLabel={{t "common.aria_labels.rescind_application"}}
    >
      <:body>
        <p class="text-lg font-semibold my-4 text-tio-gray-800">
          {{t "tuition_assistance.application_details.rescind.body"}}
        </p>
        <p class="my-4 text-tio-gray-800">
          {{t "tuition_assistance.application_details.rescind.footnote"}}
        </p>
        {{! Temporarily removing the submitError as the @error until the backend sends back a more easily consumable error message }}
        <TioErrorMessages
          @showErrors={{and this.didSubmitRescindRequest (notEq this.submitError null)}}
          {{!-- @showErrors={{true}} --}}
          @error={{t "tuition_assistance.application_details.rescind.error"}}
        />

      </:body>
      <:footer>
        <Footer
          @submitText={{t "common.confirm"}}
          @onSubmit={{this.submitRescindRequest}}
          @isRunning={{this.rescindStateAction.isRunning}}
          @onCancel={{fn (mut this.showRescindDialog) false}}
        />
      </:footer>
    </Modal>

    <Modal
      @isOpen={{this.showRepaymentDialog}}
      @onClose={{this.resetRepaymentForm}}
      @size="xl"
      @ariaLabel={{t "common.aria_labels.request_repayment"}}
      data-test-repayment-modal
    >
      <:header>
        {{t "tuition_assistance.request_repayment"}}
      </:header>
      <:body>
        <form aria-label="Employee Hold Dialog">
          <Input
            @label="Amount"
            @onChange={{this.updateRepaymentAmount}}
            @errors={{errorsForField "amount" schemaErrors=this.repaymentFormValidationErrors}}
            class="mb-4 w-full"
            @value={{divide this.requestedRepaymentAmount 100}}
            data-test-repayment-amount-input
            {{inputmask alias="currency" prefix="$" unmaskAsNumber=true digits="2"}}
          />
          <Textarea
            @label="Reason"
            @onChange={{this.updateRepaymentReason}}
            @value={{this.reasonForRepaymentRequest}}
            @errors={{errorsForField "reason" schemaErrors=this.repaymentFormValidationErrors}}
            class="mb-4 w-full"
            data-test-repayment-reason-textarea
          />
          <div class="bg-gray-100 p-4">
            <p>{{t
                "common.tuition_assistance.application_details.employee_pause_info"
                name=this.employee.firstName
              }}</p>
            <Checkbox
              @label="Employee on hold"
              @checked={{this.pauseEmployee}}
              @onChange={{this.togglePauseEmployee}}
              class="mt-4"
            />
          </div>
          <TioErrorMessages
            @showErrors={{this.didSubmitRepaymentRequest}}
            @error={{this.submitError}}
          />
        </form>
      </:body>
      <:footer>
        <Footer
          @submitDisabled={{not this.isRepaymentFormValid}}
          @onCancel={{this.toggleRepaymentDialog}}
          @onSubmit={{this.requestRepayment.perform}}
          @isRunning={{this.requestRepayment.isRunning}}
          @submitText={{t "common.request"}}
        />
      </:footer>
    </Modal>

    <Modal
      @isOpen={{this.showSettlePaymentDialog}}
      @onClose={{this.toggleSettlePaymentDialog}}
      @size="lg"
      @ariaLabel={{t "common.aria_labels.settle_payment"}}
      data-test-settlement-modal
    >
      <:header>
        <h2 class="font-semibold text-lg text-violet-800">
          {{t "common.tuition_assistance.applications.settlement_dialog.header"}}
        </h2>
      </:header>
      <:body>
        <div class="mb-12">
          <p class="font-medium">{{this.employee.fullName}}</p>
          <p class="text-gray-500">{{t
              "common.tuition_assistance.applications.settlement_dialog.id"
            }}
            {{@application.id}}</p>
        </div>
        <div class="flex w-full justify-between items-baseline">
          <p>{{t "common.tuition_assistance.applications.settlement_dialog.balance"}}</p>
          <p>{{this.amountOwed}}</p>
        </div>
        <Divider class="my-9" />

        <form class="flex flex-col -mx-4" aria-label="Settle Payment Dialog">
          <div class="flex w-full justify-between items-baseline px-4">
            <div class="min-w-48 mb-4 mr-4">
              <p>{{t "common.tuition_assistance.applications.settlement_dialog.payment"}}</p>
            </div>
            <div class="mb-4">
              <Input
                @onChange={{this.updateSettlementAmount}}
                @errors={{errorsForField "amount" schemaErrors=this.settlementFormValidationErrors}}
                class="mb-4 w-full"
                @value={{divide this.requestedSettlementAmount 100}}
                data-test-settlement-amount-input
                {{inputmask alias="currency" prefix="$" unmaskAsNumber=true digits="2"}}
              />
            </div>
          </div>
          <div class="flex">
            <Textarea
              @label="NOTE:"
              class="mb-4 px-6 w-full"
              @onChange={{this.updateSettlementReason}}
              @value={{this.reasonForSettlementRequest}}
              @errors={{errorsForField "reason" schemaErrors=this.settlementFormValidationErrors}}
              data-test-settlement-reason-textarea
            />
          </div>
          <TioErrorMessages
            @showErrors={{this.didSubmitSettlementRequest}}
            @error={{this.submitSettlementError}}
          />
        </form>
        <div class="w-full">
          <ActionableTranslation
            @t="common.tuition_assistance.applications.forgive_dialog.held_warning"
            @links={{hash
              goToEmployee=(hash
                href=(concat "/people/" this.personId "/tuition-assistance")
                t="common.tuition_assistance.applications.forgive_dialog.employee_page_link"
              )
            }}
          />
        </div>
      </:body>
      <:footer>
        <Footer
          @submitDisabled={{not this.isSettlementFormValid}}
          @onCancel={{this.toggleSettlePaymentDialog}}
          @onSubmit={{this.submitSettleRepaymentForm}}
          @isRunning={{this.requestSettlementRepayment.isRunning}}
        />
      </:footer>
    </Modal>

    <Modal
      @isOpen={{this.showForgivePaymentDialog}}
      @onClose={{this.toggleForgivePaymentDialog}}
      @size="lg"
      @ariaLabel={{t "common.aria_labels.forgive_payment"}}
      data-test-forgiveness-modal
    >
      <:header>
        <h2 class="font-semibold text-lg text-violet-800">
          {{t "common.tuition_assistance.applications.forgive_dialog.header"}}
        </h2>
      </:header>
      <:body>
        <div class="mb-12">
          <p class="font-medium">{{this.employee.fullName}}</p>
          <p class="text-gray-500">{{t "common.tuition_assistance.applications.forgive_dialog.id"}}
            {{@application.id}}</p>
        </div>
        <div class="flex w-full justify-between items-baseline">
          <p>{{t "common.tuition_assistance.applications.forgive_dialog.balance"}}</p>
          <p>{{this.amountOwed}}</p>
        </div>
        <Divider class="my-9" />

        <form class="flex flex-col -mx-4" aria-label="Forgiveness Payment Dialog">
          <div class="flex w-full justify-between items-baseline px-4">
            <div class="min-w-48 mb-4 mr-4">
              <p>{{t "common.tuition_assistance.applications.forgive_dialog.payment"}}</p>
            </div>
            <div class="mb-4">
              <Input
                @onChange={{this.updateForgivenessAmount}}
                @errors={{errorsForField
                  "amount"
                  schemaErrors=this.forgivenessFormValidationErrors
                }}
                @value={{divide this.requestedForgivenessAmount 100}}
                {{inputmask alias="currency" prefix="$" unmaskAsNumber=true digits="2"}}
                class="mb-4 w-full"
                data-test-forgiveness-amount-input
              />
            </div>
          </div>
          <div class="flex">
            <Textarea
              @label="NOTE:"
              class="mb-4 px-6 w-full"
              @onChange={{this.updateForgivenessReason}}
              @value={{this.reasonForForgivenessRequest}}
              @errors={{errorsForField "reason" schemaErrors=this.forgivenessFormValidationErrors}}
              data-test-forgiveness-reason-textarea
            />
          </div>
          <TioErrorMessages
            @showErrors={{this.didSubmitForgivenessRequest}}
            @error={{this.submitForgivenessError}}
          />
        </form>
        <div class="w-full">
          <ActionableTranslation
            @t="common.tuition_assistance.applications.forgive_dialog.held_warning"
            @links={{hash
              goToEmployee=(hash
                href=(concat "/people/" this.personId "/tuition-assistance")
                t="common.tuition_assistance.applications.forgive_dialog.employee_page_link"
              )
            }}
          />
        </div>
      </:body>

      <:footer>
        <Footer
          @submitDisabled={{not this.isForgivenessFormValid}}
          @onCancel={{this.toggleForgivePaymentDialog}}
          @onSubmit={{this.submitForgivenessRepaymentForm}}
          @isRunning={{this.requestForgivenessRepayment.isRunning}}
        />
      </:footer>
    </Modal>

    {{#if this.isModelTasApplication}}
      <EditCourseDurationDialog
        {{! @glint-expect-error: will only be tasApplication model}}
        @application={{@application}}
        @onClose={{fn (mut this.showEditCourseDurationDialog) false}}
        @isOpen={{this.showEditCourseDurationDialog}}
      />
    {{/if}}
    <Modal
      @isOpen={{this.showIgnorePoliciesModal}}
      @onClose={{this.cancelIgnorePolicies}}
      class="min-h-64"
      @ariaLabel={{t "common.aria_labels.override_policies"}}
    >
      <:body>
        <p class="text-xl my-4 text-tio-gray-800">
          {{t "common.tuition_assistance.application_details.policy_override_confirm"}}
        </p>
        <p class="mb-4 text-tio-gray-600">
          {{t "common.tuition_assistance.application_details.policy_override_description"}}
        </p>
        <p class="font-semibold italic">
          {{t "common.tuition_assistance.application_details.policy_override_warning"}}
        </p>
      </:body>
      <:footer>
        <Footer
          @isRunning={{false}}
          @onCancel={{this.cancelIgnorePolicies}}
          @onSubmit={{this.setIgnorePolicies}}
          @submitText={{t "common.confirm"}}
        />
      </:footer>
    </Modal>
  </template>
}
